/**
 * SecurityPage — a lay-person, confidence-building explanation of how AgedCareWise protects
 * people's information. Reachable from the landing-page "Security" nav link. Deliberately
 * plain-English (no IT jargon) and deliberately high-level: it reassures without handing an
 * attacker a map of the system. The deep technical evidence lives in docs/specs/*, not here.
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ShieldCheck, Lock, MapPin, Users, FileCheck2, Search, Smartphone, KeyRound,
  ArrowLeft, HeartHandshake, CheckCircle2, ScrollText,
} from 'lucide-react';

interface PromiseCard {
  icon: LucideIcon;
  title: string;
  body: string;
}

const PROMISES: PromiseCard[] = [
  {
    icon: MapPin,
    title: 'Your records are stored in Australia',
    body: 'Your profile, plans and advice records are stored securely in Australia, under Australian privacy law. The few service providers we use, and the countries they operate in, are described in our Privacy Policy.',
  },
  {
    icon: Lock,
    title: 'Encrypted — even from us',
    body: 'Your personal and financial details are scrambled with bank-grade encryption before they are stored. Even if someone got hold of the raw data, it would be unreadable without keys we keep separately.',
  },
  {
    icon: Smartphone,
    title: 'Two-factor protection',
    body: 'You can link an authenticator app to your login, so a password alone is never enough to get in. For advisers handling clients’ money, it’s required.',
  },
  {
    icon: Users,
    title: 'Only you can see your plan',
    body: 'Strict walls keep every person’s information private. One client can never see another’s, and a professional firm only ever sees its own clients — enforced by the system, not just a promise.',
  },
  {
    icon: FileCheck2,
    title: 'A tamper-proof record',
    body: 'Every important action is written to a permanent, tamper-evident log. If anything were ever changed, it would show — which keeps your advice history trustworthy.',
  },
  {
    icon: Search,
    title: 'Independently tested',
    body: 'Before we work with real clients, the system is put through a professional security review (a “penetration test”) by outside experts whose job is to try to break in.',
  },
];

export interface SecurityPageProps {
  onBack: () => void;
}

const SecurityPage = ({ onBack }: SecurityPageProps) => (
  <div className="min-h-screen bg-slate-50">
    {/* Top bar */}
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-lg font-bold">
          <span className="text-slate-900">AgedCare</span><span className="text-[#17b8e0]">Wise</span>
        </div>
      </div>
    </div>

    {/* Hero */}
    <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] text-white">
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
          <ShieldCheck className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold sm:text-4xl">Your information is safe with us</h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-white/90 sm:text-lg">
          You’re trusting AgedCareWise with sensitive details at an important time. Here’s how we protect
          them.
        </p>
      </div>
    </div>

    <div className="mx-auto max-w-5xl px-5 py-12 space-y-12">
      {/* Promise cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PROMISES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
              <Icon size={22} />
            </div>
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">{body}</p>
          </div>
        ))}
      </div>

      {/* Our approach */}
      <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-sm sm:p-9">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
            <HeartHandshake size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Our approach: plan for the worst</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
              Good security isn’t one lock on the front door — it’s many layers, designed on the assumption
              that someone <em>will</em> try to break in. So we make sure that even in a worst-case moment,
              the amount of information anyone could reach is as small as possible, and that the keys to
              unscramble it are kept well away from the data itself. It’s the same “belt-and-braces” thinking
              a bank uses.
            </p>
          </div>
        </div>
      </div>

      {/* Privacy & rights */}
      <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-sm sm:p-9">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
            <ScrollText size={24} />
          </div>
          <div className="w-full">
            <h2 className="text-xl font-bold text-slate-900">Your privacy, and your rights</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
              We follow Australia’s <strong>Privacy Act</strong>, and we only ever collect what we genuinely
              need. You stay in control of your information:
            </p>
            <ul className="mt-4 space-y-2.5">
              {[
                'You can download a copy of everything we hold about you, at any time.',
                'You can ask us to delete your information when you no longer need the service.',
                'Because we provide financial advice, we’re legally required to keep advice records for a set period — we’ll always be upfront about what’s kept and why.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Reassurance footer */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-7 text-center text-white sm:p-9">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
          <KeyRound size={20} />
        </div>
        <h2 className="text-lg font-bold sm:text-xl">Security you don’t have to think about</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/80">
          You focus on the decision in front of you. We’ll quietly do the work of keeping your details safe,
          private, and in your control — the way it should be.
        </p>
      </div>
    </div>
  </div>
);

export default SecurityPage;
